import React, { useState, useEffect } from "react";
import { User, Mail, Phone, ShieldCheck, CheckCircle2, Upload, Camera } from "lucide-react";
import api from "../services/api";
import toast, { Toaster } from "react-hot-toast";
import Sidebar from "../components/Sidebar";


export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get("/users/me");
      setProfile(res.data);
      setFullName(res.data.full_name || "");
      
      // Don't show placeholder generated emails
      const isPlaceholder = res.data.email?.includes("@placeholder.com");
      setEmail(isPlaceholder ? "" : (res.data.email || ""));
      
      setMobileNo(res.data.mobile_no || "");
    } catch (err) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      await api.put("/users/me", { 
        full_name: fullName,
        email: email || null
      });
      toast.success("Profile updated!");
      setIsEditing(false);
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    }
  };

  const handleSendOtp = async () => {
    if (!mobileNo || mobileNo.length < 10) {
      toast.error("Please enter a valid mobile number");
      return;
    }
    try {
      const res = await api.post("/users/me/send-otp", { mobile_no: mobileNo });
      toast.success(res.data.msg);
      // For development, alert the OTP
      alert(`Development Mode: Your OTP is ${res.data.otp}`);
      setOtpSent(true);
    } catch (err) {
      toast.error("Failed to send OTP");
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      toast.error("Please enter the OTP");
      return;
    }
    setVerifyingOtp(true);
    try {
      await api.post("/users/me/verify-otp", { mobile_no: mobileNo, otp });
      toast.success("Mobile number verified successfully!");
      setOtpSent(false);
      setOtp("");
      fetchProfile();
    } catch (err) {
      toast.error("Invalid OTP");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const uploadToast = toast.loading("Uploading picture...");
    try {
      const res = await api.post("/users/me/upload-picture", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Profile picture updated", { id: uploadToast });
      fetchProfile();
    } catch (err) {
      toast.error("Failed to upload picture", { id: uploadToast });
    }
  };

  if (loading) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950">
      {profile?.role !== "student" && <Sidebar role={profile?.role || "student"} />}
      <main className={`flex-1 px-4 py-8 ${profile?.role !== "student" ? "lg:ml-64" : ""} overflow-y-auto`}>
        <div className="max-w-4xl mx-auto">
          <Toaster position="top-center" />
          <h1 className="text-3xl font-black text-slate-900 dark:text-white mb-8">My Profile</h1>

      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        {/* Cover Photo */}
        <div className="h-32 bg-gradient-to-r from-blue-600 to-indigo-600"></div>
        
        <div className="px-8 pb-8 relative">
          {/* Profile Picture */}
          <div className="absolute -top-16 left-8">
            <div className="relative">
              <div className="w-32 h-32 rounded-full border-4 border-white dark:border-slate-900 bg-slate-100 dark:bg-slate-800 overflow-hidden flex items-center justify-center">
                {profile?.profile_picture ? (
                  <img src={profile.profile_picture} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <User size={48} className="text-slate-400" />
                )}
              </div>
              <label className="absolute bottom-0 right-0 p-2 bg-blue-600 rounded-full text-white cursor-pointer hover:bg-blue-700 transition-colors shadow-lg border-2 border-white dark:border-slate-900">
                <Camera size={16} />
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              </label>
            </div>
          </div>

          <div className="mt-20 flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                {profile?.full_name || "Guest User"}
              </h2>
              <div className="flex items-center gap-2 text-slate-500 mt-1">
                <ShieldCheck size={16} className="text-blue-600" />
                <span className="capitalize">{profile?.role}</span>
              </div>
            </div>
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium transition-colors"
            >
              {isEditing ? "Cancel" : "Edit Profile"}
            </button>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Personal Details */}
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800 pb-2">
                Personal Information
              </h3>
              
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600">
                  <Mail size={20} />
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium">Email Address</p>
                  <p className="text-slate-900 dark:text-white font-medium">
                    {profile?.email?.includes('@placeholder.com') ? "Not linked" : (profile?.email || "Not linked")}
                  </p>
                </div>
              </div>

              {isEditing ? (
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-500 font-medium mb-1">Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all dark:text-white"
                      placeholder="Enter your email address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-500 font-medium mb-1">Full Name</label>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all dark:text-white"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
                    Save Changes
                  </button>
                </form>
              ) : (
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600">
                    <User size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Full Name</p>
                    <p className="text-slate-900 dark:text-white font-medium">{profile?.full_name || "Not set"}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Verification Section */}
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800 pb-2">
                Verification
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-full ${profile?.mobile_verified ? 'bg-green-50 text-green-600 dark:bg-green-900/20' : 'bg-amber-50 text-amber-600 dark:bg-amber-900/20'}`}>
                    <Phone size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500 font-medium">Mobile Number</p>
                    <div className="flex items-center justify-between">
                      <p className="text-slate-900 dark:text-white font-medium">
                        {profile?.mobile_no || "Not linked"}
                      </p>
                      {profile?.mobile_verified && (
                        <span className="flex items-center gap-1 text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                          <CheckCircle2 size={14} /> Verified
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {!profile?.mobile_verified && (
                  <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                      Verify your mobile number to secure your account.
                    </p>
                    
                    {!otpSent ? (
                      <div className="flex gap-2">
                        <input
                          type="tel"
                          value={mobileNo}
                          onChange={(e) => setMobileNo(e.target.value)}
                          className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                          placeholder="Enter mobile number"
                        />
                        <button
                          onClick={handleSendOtp}
                          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shrink-0"
                        >
                          Send OTP
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                          placeholder="Enter 6-digit OTP"
                        />
                        <button
                          onClick={handleVerifyOtp}
                          disabled={verifyingOtp}
                          className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors shrink-0 disabled:opacity-50"
                        >
                          {verifyingOtp ? "Verifying..." : "Verify"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
        </div>
      </main>
    </div>
  );
}
